/**
 * SuperAdminLayout — canonical RestroPulse Super Admin shell.
 *
 * Left sidebar + top header + main content, tuned to the canonical
 * brand tokens: #FFFFFF surface, #143E30 primary, near-black text
 * (#0A0F0D), Figtree body + Cabinet Grotesk headings (inherited from
 * the global stylesheet).
 *
 * Navigation is grouped so the shell reads like a conventional
 * SaaS console. All destinations map to existing backend routes.
 */
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription,
} from '../components/ui/sheet';
import {
  LayoutDashboard, LogOut, Shield, Palette, ShieldCheck, ChefHat,
  Link2, Sparkles, Menu, KeyRound, Users, PlaySquare,
} from 'lucide-react';
import AiAlertBanner from './AiAlertBanner';

const NAV_GROUPS = [
  {
    heading: 'Management',
    items: [
      { to: '/super/dashboard',  label: 'Tenants',         icon: LayoutDashboard },
      { to: '/super/demo',       label: 'Demo Operations', icon: PlaySquare },
      { to: '/super/demo-leads', label: 'Demo Leads',      icon: Users },
    ],
  },
  {
    heading: 'Platform',
    items: [
      { to: '/super/access',   label: 'Studio Access', icon: Link2 },
      { to: '/super/branding', label: 'Branding',      icon: Palette },
      { to: '/super/features', label: 'Features',      icon: ShieldCheck },
      { to: '/super/studio',   label: 'Studio',        icon: ChefHat },
    ],
  },
  {
    heading: 'System',
    items: [
      { to: '/super/ai-usage', label: 'AI Usage & Billing', icon: Sparkles },
      { to: '/super/security', label: 'Security',           icon: KeyRound },
    ],
  },
];

const FLAT = NAV_GROUPS.flatMap((g) => g.items);


export default function SuperAdminLayout({ children }) {
  const { user, logout } = useAuth();
  const { logoUrl, platformName } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };
  const active = FLAT.find((l) => l.to === location.pathname);
  const activeLabel = active?.label || 'Super Admin';

  return (
    <div className="min-h-screen bg-[#FAFBF8] text-[#0A0F0D]"
         style={{ fontFamily: 'Figtree, ui-sans-serif, system-ui, sans-serif' }}
         data-testid="super-admin-shell">
      <AiAlertBanner />

      <div className="flex min-h-screen">
        {/* ── Desktop sidebar ───────────────────────────────────── */}
        <DesktopSidebar
          logoUrl={logoUrl}
          platformName={platformName}
          pathname={location.pathname}
        />

        {/* ── Mobile drawer trigger + content ────────────────────── */}
        <div className="flex-1 min-w-0 flex flex-col">
          <TopHeader
            title={activeLabel}
            user={user}
            onLogout={handleLogout}
            mobileOpen={mobileOpen}
            setMobileOpen={setMobileOpen}
            logoUrl={logoUrl}
            platformName={platformName}
            pathname={location.pathname}
          />
          <main className="flex-1 min-w-0"
                data-testid="super-admin-main">
            <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-10 py-8 sm:py-10">
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}


/* ── Desktop left sidebar ───────────────────────────────────────── */
function DesktopSidebar({ logoUrl, platformName, pathname }) {
  return (
    <aside data-testid="super-sidebar"
           className="hidden lg:flex w-[248px] shrink-0 bg-white border-r border-[#E7EEEB]
                       flex-col sticky top-0 h-screen">
      <div className="flex items-center gap-2.5 px-5 py-5 border-b border-[#E7EEEB]">
        <img src={logoUrl} alt={platformName}
             className="h-8 w-8 object-contain rounded" />
        <div className="min-w-0">
          <div className="text-[15px] font-bold tracking-tight leading-none text-[#0A0F0D]"
               style={{ fontFamily: 'Cabinet Grotesk, Figtree, ui-sans-serif' }}>
            {platformName}
          </div>
          <div className="mt-1.5 inline-flex items-center gap-1 text-[9px] uppercase
                          tracking-[0.18em] font-semibold text-[#143E30]
                          bg-[#E7EEEB] px-1.5 py-0.5 rounded">
            <Shield className="w-2.5 h-2.5" strokeWidth={2.25} /> Super Admin
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3"
           data-testid="super-sidebar-nav">
        {NAV_GROUPS.map((g) => (
          <div key={g.heading} className="mb-3">
            <div className="px-6 mt-3 mb-1 text-[10px] uppercase tracking-[0.22em]
                            text-[#5C6B64] font-semibold">
              {g.heading}
            </div>
            {g.items.map((item) => (
              <SidebarLink key={item.to} item={item} active={pathname === item.to} />
            ))}
          </div>
        ))}
      </nav>

      <div className="border-t border-[#E7EEEB] px-4 py-3 text-[11px] text-[#5C6B64]">
        RestroPulse operator console
      </div>
    </aside>
  );
}


function SidebarLink({ item, active }) {
  const Icon = item.icon;
  const key = item.to.split('/').pop();
  return (
    <Link to={item.to}
          data-testid={`super-nav-${key}`}
          className={`mx-3 my-0.5 px-3 py-2 rounded-md flex items-center gap-2.5
                        text-[13.5px] transition-colors ${
            active
              ? 'bg-[#E7EEEB] text-[#143E30] font-semibold'
              : 'text-[#1A2421] hover:bg-[#F4F7F5]'
          }`}>
      <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-[#143E30]' : 'text-[#5C6B64]'}`}
             strokeWidth={1.75} />
      {item.label}
      {active && (
        <span className="ml-auto w-1 h-4 rounded-sm bg-[#143E30]" aria-hidden />
      )}
    </Link>
  );
}


/* ── Top header ─────────────────────────────────────────────────── */
function TopHeader({
  title, user, onLogout, mobileOpen, setMobileOpen,
  logoUrl, platformName, pathname,
}) {
  return (
    <header data-testid="super-topbar"
            className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#E7EEEB]">
      <div className="flex items-center h-14 px-4 sm:px-6 lg:px-8 gap-3">
        {/* Mobile hamburger */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button type="button"
                    className="lg:hidden w-9 h-9 rounded-md text-[#0A0F0D]
                                hover:bg-[#F4F7F5] flex items-center justify-center"
                    aria-label="Open navigation"
                    data-testid="super-mobile-nav-trigger">
              <Menu className="w-5 h-5" strokeWidth={1.75} />
            </button>
          </SheetTrigger>
          <SheetContent side="left"
                        data-testid="super-mobile-nav-drawer"
                        className="w-[280px] sm:w-[300px] p-0 bg-white border-[#E7EEEB]">
            <SheetTitle className="sr-only">Super Admin navigation</SheetTitle>
            <SheetDescription className="sr-only">
              RestroPulse Super Admin navigation for tenants, demo operations, and platform settings.
            </SheetDescription>
            <div className="flex flex-col h-full">
              <div className="flex items-center gap-2.5 px-5 py-5 border-b border-[#E7EEEB]">
                <img src={logoUrl} alt={platformName}
                     className="h-8 w-8 object-contain rounded" />
                <div>
                  <div className="text-[15px] font-bold tracking-tight leading-none text-[#0A0F0D]">
                    {platformName}
                  </div>
                  <div className="mt-1.5 inline-flex items-center gap-1 text-[9px] uppercase
                                    tracking-[0.18em] font-semibold text-[#143E30]
                                    bg-[#E7EEEB] px-1.5 py-0.5 rounded">
                    <Shield className="w-2.5 h-2.5" strokeWidth={2.25} /> Super Admin
                  </div>
                </div>
              </div>
              <nav className="flex-1 overflow-y-auto py-3"
                   data-testid="super-mobile-nav-links">
                {NAV_GROUPS.map((g) => (
                  <div key={g.heading} className="mb-3">
                    <div className="px-6 mt-3 mb-1 text-[10px] uppercase tracking-[0.22em]
                                    text-[#5C6B64] font-semibold">{g.heading}</div>
                    {g.items.map((item) => {
                      const Icon = item.icon;
                      const active = pathname === item.to;
                      const key = item.to.split('/').pop();
                      return (
                        <Link key={item.to} to={item.to}
                              onClick={() => setMobileOpen(false)}
                              data-testid={`super-mobile-nav-${key}`}
                              className={`mx-3 my-0.5 px-3 py-2.5 rounded-md flex items-center gap-2.5
                                            text-[14px] ${
                                active
                                  ? 'bg-[#E7EEEB] text-[#143E30] font-semibold'
                                  : 'text-[#1A2421] hover:bg-[#F4F7F5]'
                              }`}>
                          <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-[#143E30]' : 'text-[#5C6B64]'}`}
                                 strokeWidth={1.75} />
                          {item.label}
                        </Link>
                      );
                    })}
                  </div>
                ))}
              </nav>
              <div className="border-t border-[#E7EEEB] px-4 py-4">
                <Button variant="outline" size="sm"
                        onClick={() => { setMobileOpen(false); onLogout(); }}
                        className="w-full justify-center border-[#E7EEEB]
                                    text-[#0A0F0D] hover:bg-[#F4F7F5] h-9"
                        data-testid="super-mobile-logout-button">
                  <LogOut className="w-4 h-4 mr-2" strokeWidth={1.75} /> Sign out
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        <h1 className="text-[15px] sm:text-[16px] font-semibold tracking-tight text-[#0A0F0D]"
            style={{ fontFamily: 'Cabinet Grotesk, Figtree, ui-sans-serif' }}
            data-testid="super-page-title">
          {title}
        </h1>

        <div className="ml-auto flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-2 text-[13px] text-[#0A0F0D]">
            <div className="w-7 h-7 rounded-full bg-[#E7EEEB] text-[#143E30] font-semibold
                            text-[12px] flex items-center justify-center">
              {(user?.name || user?.email || 'S').charAt(0).toUpperCase()}
            </div>
            <span className="truncate max-w-[160px]">{user?.name || user?.email}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={onLogout}
                  className="h-8 w-8 p-0 text-[#5C6B64] hover:text-[#B91C1C] hover:bg-[#FEF2F2]"
                  data-testid="super-logout-button"
                  title="Sign out">
            <LogOut className="w-4 h-4" strokeWidth={1.75} />
          </Button>
        </div>
      </div>
    </header>
  );
}
